import { AppBar, IconButton, Toolbar, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import LoginPage from './pages/LoginPage';
import MonitoringSystemPage from './pages/MonitoringSystemPage';

import { faHouseSignal, faMapLocationDot, faUser } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Grid from '@mui/material/Grid2';
import axios, { AxiosResponse } from 'axios';
import { useTranslation } from 'react-i18next';
import packageJson from '../package.json';
import { getFromEndpointWithToken, postToEndpointWithToken } from './AxiosHelper';
import { DeviceWithState } from './Device';
import { clearFCMToken, getFCMToken, initializeFirebase, refreshFCMToken } from './Firebase';
import { isStringNullUndefinedOrEmpty } from './Helper';
import { MonitoringSystemDescriptor, MonitoringSystemState } from './MonitoringSystem';
import { MonitoringSystemSelector } from './MonitoringSystemSelector';
import { User } from './User';
import LanguageSelector from './components/LanguageSelector';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import MapPage from './pages/MapPage';
import SettingsPage from './pages/SettingsPage';
import VerifyPage from './pages/VerifyPage';

const VERSION = packageJson.version;

function App(): JSX.Element {
  const LOGIN_TOKEN_KEY = 'fbpAuthToken';
  const { t, i18n } = useTranslation();
  const [token, setToken] = useState<string | undefined>(localStorage.getItem(LOGIN_TOKEN_KEY) ?? undefined);
  const [user, setUser] = useState<User | undefined>(undefined);
  const [monitoringSystemDescriptors, setMonitoringSystemDescriptors] = useState<MonitoringSystemDescriptor[]>([]);
  const [selectedMonitoringSystemDescriptorId, setSelectedMonitoringSystemDescriptorId] = useState<number | undefined>(undefined);
  const [selectedMonitoringSystemState, setSelectedMonitoringSystemState] = useState<MonitoringSystemState | undefined>(undefined);
  const location = useLocation();
  const navigate = useNavigate();

  //call it only once on page open to init notifications through firebase
  useEffect(() => initializeFirebase(), []);

  const handleError = useCallback((error: any) => {
    if (axios.isAxiosError(error)) {
      //any axios error means logout on this level
      setToken(undefined);
    }
  }, []);

  const ownMonitoringSystemId = useMemo(() => {
    return monitoringSystemDescriptors.findIndex((system) => system.owner.email === user?.email);
  }, [monitoringSystemDescriptors, user?.email]);

  const ownMonitoringSystemDescriptor = ownMonitoringSystemId === -1 ? undefined : monitoringSystemDescriptors[ownMonitoringSystemId];

  const getFromEndpointForToken = useCallback((endpoint: string): Promise<AxiosResponse<any, any>> => {
    if (token !== undefined) {
      return getFromEndpointWithToken(endpoint, token);
    } else {
      return Promise.reject("No token");
    }
  }, [token]);

  const pollMonitoringSystemState = useCallback(() => {
    if (selectedMonitoringSystemDescriptorId !== undefined) {
      getFromEndpointForToken('getMonitoringSystemState?monitoringSystemId=' + selectedMonitoringSystemDescriptorId)
        .then((response) => {
          setSelectedMonitoringSystemState(response?.data);
        })
        .catch(handleError)
    }
  }, [getFromEndpointForToken, handleError, selectedMonitoringSystemDescriptorId]);

  const updateUser = useCallback(() => {
    getFromEndpointForToken('getCurrentUser')
      .then((response) => {
        let user = response?.data as User;

        if (isStringNullUndefinedOrEmpty(user.nickName)) {
          //set the first part of the email address as a default user name if it not yet set
          user.nickName = user.email.split('@')[0];
        }

        setUser(user);
      })
      .catch(handleError);
  }, [getFromEndpointForToken, handleError]);

  const renewLoginToken = (token: string) => {
    postToEndpointWithToken('refreshToken', token, { AuthToken: token })
      .then(response => setToken(response?.data))
      .catch(reason => console.error('Token could not be renewed. Reason: ' + reason));
  }

  //do it once on page open (refresh login token if there is any)
  useEffect(() => {
    if (token !== undefined) {
      renewLoginToken(token);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //update user once the token changes
  useEffect(() => {
    if (token === undefined) {
      localStorage.removeItem(LOGIN_TOKEN_KEY);
      setUser(undefined);
    } else {
      localStorage.setItem(LOGIN_TOKEN_KEY, token);
      refreshFCMToken(token);
      updateUser();
    }
  }, [token, updateUser])

  //Poll monitoring system state periodically
  useEffect(() => {
    pollMonitoringSystemState();//do it once on page open and then periodically

    const interval = setInterval(() => {
      pollMonitoringSystemState();
    }, 5000);

    return () => {
      clearInterval(interval);
    }
  }, [pollMonitoringSystemState]);

  const updateMonitoringSystemDescriptors = useCallback(() => {
    getFromEndpointForToken('getMonitoringSystemDescriptors')
      .then((descriptors) => {
        const newDs = (descriptors.data as MonitoringSystemDescriptor[]).map((d) => {
          const newD = d;

          if (isStringNullUndefinedOrEmpty(newD.owner.nickName)) {
            //if owner's user name is missing, we generate it just like for the current user
            newD.owner.nickName = newD.owner.email.split('@')[0];
          }

          newD.guests = newD.guests.map((g) => {
            const newG = g;
            if (isStringNullUndefinedOrEmpty(newG.nickName)) {
              //if guest's user name is missing, we generate it just like for the current user
              newG.nickName = newG.email.split('@')[0];
            }
            return newG;
          });

          //always override monitoring system name based on user name
          newD.name = newD.owner.email === user?.email ? t('own_ms_name') : t('default_ms_name', { userName: newD.owner.nickName });

          newD.deviceDescriptors = newD.deviceDescriptors.map((dd) => {
            const newDD = dd;
            if (isStringNullUndefinedOrEmpty(newDD.name)) {
              //if device name is missing, we generate it based on its ID
              newDD.name = t('default_device_name', { id: newDD.deviceId })
            }
            return newDD;
          })

          return newD;
        });

        setMonitoringSystemDescriptors(newDs);
        if (newDs.find(descriptor => descriptor.monitoringSystemId === selectedMonitoringSystemDescriptorId) === undefined) {
          //if the selected monitoring system is not present anymore, we select the own monitoring system
          setSelectedMonitoringSystemDescriptorId(ownMonitoringSystemDescriptor?.monitoringSystemId);
        }
      })
      .catch(handleError);
  }, [getFromEndpointForToken, handleError, ownMonitoringSystemDescriptor?.monitoringSystemId, selectedMonitoringSystemDescriptorId, t, user?.email])

  //obtain monitoring system descriptors once a new user is present
  //clear monitoring system descriptors once a user is not present anymore
  useEffect(() => {
    if (user !== undefined) {
      updateMonitoringSystemDescriptors();
    } else {
      setSelectedMonitoringSystemDescriptorId(undefined);
      setMonitoringSystemDescriptors([]);
    }
  }, [updateMonitoringSystemDescriptors, user]);

  //select own monitoring system once the monitoring systems are loaded
  useEffect(() => {
    if (selectedMonitoringSystemDescriptorId === undefined && ownMonitoringSystemDescriptor !== undefined) {
      setSelectedMonitoringSystemDescriptorId(ownMonitoringSystemDescriptor.monitoringSystemId);
    }
  }, [ownMonitoringSystemDescriptor, selectedMonitoringSystemDescriptorId]);

  const selectedMonitoringSystemDescriptor = useMemo(() => {
    return monitoringSystemDescriptors.find(ms => ms.monitoringSystemId === selectedMonitoringSystemDescriptorId);
  }, [monitoringSystemDescriptors, selectedMonitoringSystemDescriptorId]);

  const getDeviceDescriptorForDeviceId = useCallback((id: number) => {
    return selectedMonitoringSystemDescriptor?.deviceDescriptors.find(dd => dd.deviceId === id);
  }, [selectedMonitoringSystemDescriptor?.deviceDescriptors]);

  const deviceWithStatesWithLocation = useMemo(() => {
    const deviceStatesWithLocation = selectedMonitoringSystemState?.deviceStates.filter((ds) => ds.latitude !== null && ds.latitude !== undefined && ds.longitude !== null && ds.longitude !== undefined && getDeviceDescriptorForDeviceId(ds.deviceId) !== undefined);

    if (deviceStatesWithLocation === undefined) {
      return [];
    }

    return deviceStatesWithLocation.map((ds) => {
      const deviceDescriptor = getDeviceDescriptorForDeviceId(ds.deviceId);
      return { deviceDescriptor: deviceDescriptor, deviceState: ds } as DeviceWithState;
    })
  }, [getDeviceDescriptorForDeviceId, selectedMonitoringSystemState?.deviceStates])

  useEffect(() => {
    console.log("Version: " + VERSION);
  }, [])

  const handleLogout = useCallback(() => {
    if (token !== undefined) {
      postToEndpointWithToken('logout', token, { FCMToken: getFCMToken() ?? '' })
        .then(() => {
          clearFCMToken();
          setToken(undefined);
        })
        .catch((error) => {
          console.error('Error during logout:', error);
          clearFCMToken();
          setToken(undefined);
        })
    }
  }, [token]);

  return (
    <Grid container direction='column'>
      <PWAInstallPrompt />
      <Grid>
        <AppBar position="sticky" style={{ marginBottom: 10 }}>
          <Toolbar>
            <Grid container alignItems='center' justifyContent='flex-start' width='100%'>
              <Grid size='auto'>
                <Typography
                  variant="h6"
                  style={{ textDecoration: 'none', color: 'inherit' }}
                  component={Link}
                  to={monitoringSystemDescriptors.length > 0 ? "/monitor-list" : "/login"}
                >
                  Sam Point
                </Typography>
              </Grid>
              <Grid size='grow'>
                <Grid container alignItems='center' justifyContent='flex-end'>
                  {monitoringSystemDescriptors.length > 1 &&
                    <Grid>
                      <MonitoringSystemSelector
                        onSelectionChange={(msdid) => {
                          setSelectedMonitoringSystemDescriptorId(msdid);
                          navigate('/monitor-list')
                        }}
                        selectedMonitoringSystemId={selectedMonitoringSystemDescriptor?.monitoringSystemId}
                        ownMonitoringSystemId={ownMonitoringSystemDescriptor?.monitoringSystemId}
                        monitoringSystemDescriptorsForSelection={monitoringSystemDescriptors.map(ms => { return { id: ms.monitoringSystemId, name: ms.name } })} />
                    </Grid>
                  }
                  {selectedMonitoringSystemDescriptor !== undefined && <Grid>
                    <IconButton edge="end" color="inherit" component={Link} to="/monitor-list">
                      <FontAwesomeIcon icon={faHouseSignal} />
                    </IconButton>
                  </Grid>}
                  {selectedMonitoringSystemDescriptor !== undefined && <Grid>
                    <IconButton
                      edge="end"
                      color="inherit"
                      component={Link}
                      to="/map">
                      <FontAwesomeIcon icon={faMapLocationDot} />
                    </IconButton>
                  </Grid>}
                  {user !== undefined && <Grid>
                    <IconButton edge="end" color="inherit" component={Link} to="/user" title={user.nickName}>
                      <FontAwesomeIcon icon={faUser} />
                    </IconButton>
                  </Grid>}
                  <Grid>
                    <LanguageSelector languageAbbreviation={i18n.language} onLanguageChange={(newLanguage) => i18n.changeLanguage(newLanguage)} />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Toolbar>
        </AppBar>
      </Grid>
      <Grid>
        <Routes>
          {/*Default route redirects to login*/}
          <Route
            path="/"
            element={<Navigate to='/login' />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
          {/*Login redirects to monitor-list if user is logged in and all necessary info is available*/}
          {/*Furthermore, it passes the addingCode from the redirected URL to the monitor-list page, if any */}
          <Route
            path="/login"
            // state={{ from: location }} is used to further preserve the potentially preserved addingCode from the URL before re-redirecting to monitor-list after redirecting to login
            element={token === undefined ? <LoginPage onLogin={setToken} /> : (
              user !== undefined &&
              selectedMonitoringSystemDescriptor !== undefined &&
              selectedMonitoringSystemState !== undefined &&
              <Navigate to={`/monitor-list`} state={{ from: location }} />)} />
          { /*monitor-list redirects to login if user is not logged in*/}
          <Route
            path="/monitor-list"
            // state={{ from: location }} is used to preserve the potential addingCode from the URL before redirecting to login
            element={token === undefined ? <Navigate to='/login' state={{ from: location }} /> : (
              user !== undefined &&
              selectedMonitoringSystemDescriptor !== undefined &&
              selectedMonitoringSystemState !== undefined &&
              <MonitoringSystemPage
                token={token}
                user={user}
                selectedMonitoringSystemDescriptor={selectedMonitoringSystemDescriptor}
                selectedMonitoringSystemState={selectedMonitoringSystemState}
                updateMonitoringSystemDescriptorRequest={updateMonitoringSystemDescriptors}
                onLogout={handleLogout} />)} />
          <Route
            path="/verify"
            element={<VerifyPage token={token} requestLogout={handleLogout} />} />
          { /* Map page redirects to login if user is not logged in and to monitor-list if there is nothing to show on map */}
          <Route
            path="/map"
            element={token === undefined ?
              <Navigate to='/login' state={{ from: location }} /> :
              (user !== undefined &&
                selectedMonitoringSystemDescriptor !== undefined &&
                selectedMonitoringSystemState !== undefined &&
                (deviceWithStatesWithLocation ?
                  <MapPage devicesWithState={deviceWithStatesWithLocation} /> :
                  <Navigate to='/monitor-list' state={{ from: location }} />))} />
          { /* user page redirects to login if user is not logged in */}
          <Route
            path="/user"
            element={token === undefined ? <Navigate to='/login' state={{ from: location }} /> :
              (user !== undefined && <SettingsPage
                token={token}
                user={user}
                hasDevices={(ownMonitoringSystemDescriptor?.deviceDescriptors.length ?? 0) > 0}
                onRequestUserUpdate={updateUser}
                onLogout={handleLogout} />)} />
        </Routes>
      </Grid>
    </Grid >
  );
}

export default App;
